import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getRecommendationCountExact, loadFavoritesFromSupabase, loadWatchedFromSupabase, loadRecommendationsFromSupabase } from '../services/supabaseDataService';

/**
 * Görev 4: İstatistik hook'u
 * 
 * Veriyi sadece kullanıcı hazırken çeker.
 * - authStatus === 'authenticated' ve user?.id varsa veri çeker
 * - Focus/visibility değişikliklerinde tazeler
 * - Retry mekanizması ile güvenilir veri yükleme
 */
const useStats = () => {
    const { user, authStatus } = useAuth();
    
    const [stats, setStats] = useState({
        totalRecommendations: 0,
        totalFavorites: 0,
        watchedThisMonth: 0,
        totalWatched: 0,
        recentRecommendations: [],
        favoriteGenres: [],
        loading: true,
        error: null
    });

    const loadStats = useCallback(async (retryCount = 0) => {
        // Kullanıcı hazır değilse bekle
        if (authStatus !== 'authenticated' || !user?.id) {
            if (retryCount < 5) {
                setTimeout(() => loadStats(retryCount + 1), 300);
            } else {
                setStats(prev => ({ ...prev, loading: false }));
            }
            return;
        }

        const userId = user.id;

        try {
            setStats(prev => ({ ...prev, loading: true, error: null }));

            // Paralel olarak tüm verileri çek
            const [favoritesFromSupabase, watchedFromSupabase, recommendationsFromSupabase, recCount] = await Promise.all([
                loadFavoritesFromSupabase(userId).catch(() => []),
                loadWatchedFromSupabase(userId).catch(() => []),
                loadRecommendationsFromSupabase(userId).catch(() => []),
                getRecommendationCountExact().catch(() => 0)
            ]);

            // İstatistikleri hesapla
            const favCount = favoritesFromSupabase.length;
            const totalWatched = watchedFromSupabase.length;

            // Bu ay izlenen film sayısı
            const thisMonth = new Date().getMonth();
            const thisYear = new Date().getFullYear();
            const watchedThisMonthCount = watchedFromSupabase.filter(movie => {
                if (!movie.watchedAt) return false;
                const watchedDate = new Date(movie.watchedAt);
                return watchedDate.getMonth() === thisMonth && watchedDate.getFullYear() === thisYear;
            }).length;

            // Favori türleri hesapla
            const genreCount = {};
            favoritesFromSupabase.forEach(fav => {
                const genre = fav.genre || fav.movie_genre;
                if (genre) {
                    genreCount[genre] = (genreCount[genre] || 0) + 1;
                }
            });
            const favoriteGenres = Object.entries(genreCount)
                .map(([genre, count]) => ({ genre, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 3);

            // Son önerileri al
            let recentRecs = [];
            if (recommendationsFromSupabase.length > 0) {
                const allMovies = [];
                recommendationsFromSupabase.forEach(rec => {
                    const movies = rec.movies || rec.recommendations || rec.recommendation_data || [];
                    if (Array.isArray(movies)) {
                        movies.forEach(movie => {
                            allMovies.push({
                                ...movie,
                                recommendedAt: rec.createdAt || rec.created_at || new Date().toISOString()
                            });
                        });
                    } else if (typeof movies === 'object') {
                        allMovies.push({
                            ...movies,
                            recommendedAt: rec.createdAt || rec.created_at || new Date().toISOString()
                        });
                    }
                });
                recentRecs = allMovies.slice(0, 3);
            }

            setStats({
                totalRecommendations: recCount,
                totalFavorites: favCount,
                watchedThisMonth: watchedThisMonthCount,
                totalWatched: totalWatched,
                recentRecommendations: recentRecs,
                favoriteGenres,
                loading: false,
                error: null
            });

        } catch (error) {
            console.error('❌ İstatistik yükleme hatası:', error);
            setStats(prev => ({
                ...prev,
                loading: false,
                error: error.message || 'Veriler yüklenirken bir hata oluştu'
            }));
        }
    }, [user?.id, authStatus]);

    // Kullanıcı hazır olduğunda verileri yükle
    useEffect(() => {
        if (authStatus === 'authenticated' && user?.id) {
            loadStats();
        }
    }, [authStatus, user?.id, loadStats]);

    // Görev 6: Focus/visibility değişikliklerinde tazele
    useEffect(() => {
        if (authStatus !== 'authenticated' || !user?.id) return;

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                loadStats();
            }
        };

        const handleFocus = () => {
            loadStats();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [authStatus, user?.id, loadStats]);

    return {
        ...stats,
        refresh: loadStats
    };
};

export default useStats;
